import { useState, useEffect, useCallback, useRef } from 'react'
import { isUserAdmin, refreshUserToken } from '../firebase/auth'
import { createProduct, getProducts, deleteProduct, subscribeToProducts, updateProduct } from '../firebase/products'
import { getClaudeApiKey, getClaudeDefaultPrompt, saveClaudeSettings, getFtpSettings, saveFtpSettings, getHtmlTemplate, saveHtmlTemplate, getHtmlIndexTemplate, saveHtmlIndexTemplate, getDevSecOpsArticleIndexTemplates, saveDevSecOpsArticleIndexTemplates, getSitemapTemplate, saveSitemapTemplate, getLinkedInSettings, saveLinkedInSettings } from '../firebase/settings'
import './Admin.css'

const Admin = ({ user }) => {
  const [isAdmin, setIsAdmin] = useState(false)
  const [loading, setLoading] = useState(true)
  const [products, setProducts] = useState([])
  const [showProductForm, setShowProductForm] = useState(false)
  const [editingProductId, setEditingProductId] = useState(null)
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    url: '',
    ctaButtonUrl: '',
    ctaButtonText: '',
    ctaNote: '',
    demoButtonUrl: '',
    demoButtonText: '',
    demoNote: ''
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [tokenClaims, setTokenClaims] = useState(null)
  const [claudeApiKey, setClaudeApiKey] = useState('')
  const [claudeApiKeyInput, setClaudeApiKeyInput] = useState('')
  const [claudeDefaultPrompt, setClaudeDefaultPrompt] = useState('')
  const [savingApiKey, setSavingApiKey] = useState(false)
  const [apiKeyError, setApiKeyError] = useState(null)
  const [apiKeySuccess, setApiKeySuccess] = useState(false)
  const [ftpHost, setFtpHost] = useState('')
  const [ftpPort, setFtpPort] = useState('')
  const [ftpUsername, setFtpUsername] = useState('')
  const [ftpDestinationFolder, setFtpDestinationFolder] = useState('')
  const [ftpPassword, setFtpPassword] = useState('')
  const [ftpPasswordInput, setFtpPasswordInput] = useState('')
  const [savingFtp, setSavingFtp] = useState(false)
  const [ftpError, setFtpError] = useState(null)
  const [ftpSuccess, setFtpSuccess] = useState(false)
  const [htmlTemplate, setHtmlTemplate] = useState('')
  const [savingHtml, setSavingHtml] = useState(false)
  const [htmlError, setHtmlError] = useState(null)
  const [htmlSuccess, setHtmlSuccess] = useState(false)
  const [sitemapTemplate, setSitemapTemplate] = useState('')
  const [savingSitemap, setSavingSitemap] = useState(false)
  const [sitemapError, setSitemapError] = useState(null)
  const [sitemapSuccess, setSitemapSuccess] = useState(false)
  const [htmlIndexTemplate, setHtmlIndexTemplate] = useState('')
  const [savingHtmlIndex, setSavingHtmlIndex] = useState(false)
  const [htmlIndexError, setHtmlIndexError] = useState(null)
  const [htmlIndexSuccess, setHtmlIndexSuccess] = useState(false)
  const [devSecOpsDevTemplate, setDevSecOpsDevTemplate] = useState('')
  const [devSecOpsSecTemplate, setDevSecOpsSecTemplate] = useState('')
  const [devSecOpsOpsTemplate, setDevSecOpsOpsTemplate] = useState('')
  const [savingDevSecOpsTemplates, setSavingDevSecOpsTemplates] = useState(false)
  const [devSecOpsTemplatesError, setDevSecOpsTemplatesError] = useState(null)
  const [devSecOpsTemplatesSuccess, setDevSecOpsTemplatesSuccess] = useState(false)
  const [linkedInClientId, setLinkedInClientId] = useState('')
  const [linkedInPrimaryClientSecret, setLinkedInPrimaryClientSecret] = useState('')
  const [linkedInPrimaryClientSecretInput, setLinkedInPrimaryClientSecretInput] = useState('')
  const [linkedInSecondaryClientSecret, setLinkedInSecondaryClientSecret] = useState('')
  const [linkedInSecondaryClientSecretInput, setLinkedInSecondaryClientSecretInput] = useState('')
  const [linkedInAuthorizedRedirectUrls, setLinkedInAuthorizedRedirectUrls] = useState('')
  const [linkedInOrganizationId, setLinkedInOrganizationId] = useState('')
  const [linkedInCompanyPageUrl, setLinkedInCompanyPageUrl] = useState('')
  const [savingLinkedIn, setSavingLinkedIn] = useState(false)
  const [linkedInError, setLinkedInError] = useState(null)
  const [linkedInSuccess, setLinkedInSuccess] = useState(false)
  const productFormRef = useRef(null)

  const checkAdminStatus = useCallback(async (forceRefresh = true) => {
    if (user) {
      setLoading(true)
      try {
        // Force refresh token to get latest custom claims
        await refreshUserToken(user)
        const adminStatus = await isUserAdmin(user, forceRefresh)
        setIsAdmin(adminStatus)
        
        // Get token claims for debugging
        try {
          const tokenResult = await user.getIdTokenResult(forceRefresh)
          setTokenClaims(tokenResult.claims)
          console.log('Admin component - Current token claims:', tokenResult.claims)
          console.log('Admin status:', adminStatus)
        } catch (error) {
          console.error('Error getting token claims:', error)
        }
      } catch (error) {
        console.error('Error checking admin status:', error)
      } finally {
        setLoading(false)
      }
    } else {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    checkAdminStatus()
  }, [checkAdminStatus])

  // Scroll to form when it appears
  useEffect(() => {
    if (showProductForm && productFormRef.current) {
      setTimeout(() => {
        productFormRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        })
      }, 100)
    }
  }, [showProductForm])

  useEffect(() => {
    if (isAdmin) {
      // Subscribe to products in real-time
      const unsubscribe = subscribeToProducts((productsList) => {
        setProducts(productsList)
      })
      
      // Load Claude settings
      const loadClaudeSettings = async () => {
        try {
          const [apiKey, defaultPrompt] = await Promise.all([
            getClaudeApiKey(),
            getClaudeDefaultPrompt()
          ])
          
          if (apiKey) {
            setClaudeApiKey(apiKey)
            // Don't populate the input field for security - user needs to re-enter to update
            setClaudeApiKeyInput('')
          }
          
          if (defaultPrompt) {
            setClaudeDefaultPrompt(defaultPrompt)
          }
        } catch (err) {
          console.error('Error loading Claude settings:', err)
        }
      }
      
      // Load FTP settings
      const loadFtpSettings = async () => {
        try {
          const ftpSettings = await getFtpSettings()
          
          if (ftpSettings) {
            setFtpHost(ftpSettings.host || '')
            setFtpPort(ftpSettings.port || '')
            setFtpUsername(ftpSettings.username || '')
            setFtpDestinationFolder(ftpSettings.destinationFolder || '')
            // Don't populate password field for security - user needs to re-enter to update
            setFtpPasswordInput('')
            if (ftpSettings.password) {
              setFtpPassword(ftpSettings.password) // This will be '***' to indicate password is saved
            }
          }
        } catch (err) {
          console.error('Error loading FTP settings:', err)
        }
      }
      
      // Load HTML template
      const loadHtmlTemplate = async () => {
        try {
          const template = await getHtmlTemplate()
          
          if (template) {
            setHtmlTemplate(template)
          }
        } catch (err) {
          console.error('Error loading HTML template:', err)
        }
      }
      
      // Load sitemap template
      const loadSitemapTemplate = async () => {
        try {
          const template = await getSitemapTemplate()

          if (template) {
            setSitemapTemplate(template)
          }
        } catch (err) {
          console.error('Error loading sitemap template:', err)
        }
      }

      // Load HTML index template (for listing articles)
      const loadHtmlIndexTemplate = async () => {
        try {
          const template = await getHtmlIndexTemplate()

          if (template) {
            setHtmlIndexTemplate(template)
          }
        } catch (err) {
          console.error('Error loading HTML index template:', err)
        }
      }

      // Load DevSecOps article index templates
      const loadDevSecOpsArticleIndexTemplates = async () => {
        try {
          const templates = await getDevSecOpsArticleIndexTemplates()
          setDevSecOpsDevTemplate(templates.dev || '')
          setDevSecOpsSecTemplate(templates.sec || '')
          setDevSecOpsOpsTemplate(templates.ops || '')
        } catch (err) {
          console.error('Error loading DevSecOps article index templates:', err)
        }
      }

      // Load LinkedIn settings
      const loadLinkedInSettings = async () => {
        try {
          const linkedInSettings = await getLinkedInSettings()
          
          if (linkedInSettings) {
            setLinkedInClientId(linkedInSettings.clientId || '')
            setLinkedInAuthorizedRedirectUrls(linkedInSettings.authorizedRedirectUrls || '')
            setLinkedInOrganizationId(linkedInSettings.organizationId || '')
            setLinkedInCompanyPageUrl(linkedInSettings.companyPageUrl || '')
            // Don't populate secret fields for security - user needs to re-enter to update
            setLinkedInPrimaryClientSecretInput('')
            setLinkedInSecondaryClientSecretInput('')
            if (linkedInSettings.primaryClientSecret) {
              setLinkedInPrimaryClientSecret('***') // This will be '***' to indicate secret is saved
            }
            if (linkedInSettings.secondaryClientSecret) {
              setLinkedInSecondaryClientSecret('***') // This will be '***' to indicate secret is saved
            }
          }
        } catch (err) {
          console.error('Error loading LinkedIn settings:', err)
        }
      }
      
      loadClaudeSettings()
      loadFtpSettings()
      loadHtmlTemplate()
      loadSitemapTemplate()
      loadHtmlIndexTemplate()
      loadDevSecOpsArticleIndexTemplates()
      loadLinkedInSettings()
      
      return () => unsubscribe()
    }
  }, [isAdmin])

  const handleProductFormChange = (e) => {
    const { name, value } = e.target
    setProductForm(prev => ({
      ...prev,
      [name]: value
    }))
    setError(null)
  }

  const handleProductSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    // Validation
    if (!productForm.name.trim()) {
      setError('Product name is required')
      return
    }

    if (!productForm.url.trim()) {
      setError('Product URL is required')
      return
    }

    // Basic URL validation
    try {
      new URL(productForm.url)
    } catch {
      setError('Please enter a valid URL (e.g., https://example.com)')
      return
    }

    setSubmitting(true)
    try {
      const productData = {
        name: productForm.name.trim(),
        description: productForm.description.trim(),
        url: productForm.url.trim(),
        ctaButtonUrl: productForm.ctaButtonUrl.trim(),
        ctaButtonText: productForm.ctaButtonText.trim(),
        ctaNote: productForm.ctaNote.trim(),
        demoButtonUrl: productForm.demoButtonUrl.trim(),
        demoButtonText: productForm.demoButtonText.trim(),
        demoNote: productForm.demoNote.trim()
      }

      if (editingProductId) {
        // Update existing product
        await updateProduct(editingProductId, productData)
      } else {
        // Create new product
        await createProduct(productData)
      }

      // Reset form
      setProductForm({
        name: '',
        description: '',
        url: '',
        ctaButtonUrl: '',
        ctaButtonText: '',
        ctaNote: '',
        demoButtonUrl: '',
        demoButtonText: '',
        demoNote: ''
      })
      setEditingProductId(null)
      setShowProductForm(false)
    } catch (err) {
      console.error('Error saving product:', err)
      setError(editingProductId ? 'Failed to update product. Please try again.' : 'Failed to create product. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleEditProduct = (product) => {
    console.log('handleEditProduct called with product:', product)
    if (!product || !product.id) {
      console.error('Invalid product data:', product)
      return
    }
    const formData = {
      name: product.name || '',
      description: product.description || '',
      url: product.url || '',
      ctaButtonUrl: product.ctaButtonUrl || '',
      ctaButtonText: product.ctaButtonText || '',
      ctaNote: product.ctaNote || '',
      demoButtonUrl: product.demoButtonUrl || '',
      demoButtonText: product.demoButtonText || '',
      demoNote: product.demoNote || ''
    }
    console.log('Setting form data:', formData)
    console.log('Setting editingProductId to:', product.id)
    setProductForm(formData)
    setEditingProductId(product.id)
    setShowProductForm(true)
    setError(null)
    console.log('State updates triggered')
  }

  const handleCancelEdit = () => {
    setProductForm({
      name: '',
      description: '',
      url: '',
      ctaButtonUrl: '',
      ctaButtonText: '',
      ctaNote: '',
      demoButtonUrl: '',
      demoButtonText: '',
      demoNote: ''
    })
    setEditingProductId(null)
    setShowProductForm(false)
    setError(null)
  }

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return
    }

    try {
      await deleteProduct(productId)
    } catch (err) {
      console.error('Error deleting product:', err)
      alert('Failed to delete product. Please try again.')
    }
  }

  const handleClaudeApiKeyChange = (e) => {
    setClaudeApiKeyInput(e.target.value)
    setApiKeyError(null)
    setApiKeySuccess(false)
  }

  const handleClaudeDefaultPromptChange = (e) => {
    setClaudeDefaultPrompt(e.target.value)
    setApiKeyError(null)
    setApiKeySuccess(false)
  }

  const handleSaveClaudeSettings = async (e) => {
    e.preventDefault()
    setApiKeyError(null)
    setApiKeySuccess(false)

    // Only require API key if it's being updated (user entered something)
    if (claudeApiKeyInput.trim() && claudeApiKeyInput.trim().length < 10) {
      setApiKeyError('API key appears to be invalid (too short)')
      return
    }

    setSavingApiKey(true)
    try {
      const settingsToSave = {}
      
      // Only save API key if user entered a new one
      if (claudeApiKeyInput.trim()) {
        settingsToSave.claudeApiKey = claudeApiKeyInput.trim()
        setClaudeApiKey(claudeApiKeyInput.trim())
        // Clear input after saving for security
        setClaudeApiKeyInput('')
      }
      
      // Always save the default prompt (even if empty, to allow clearing it)
      settingsToSave.claudeDefaultPrompt = claudeDefaultPrompt.trim()
      
      await saveClaudeSettings(settingsToSave)
      setApiKeySuccess(true)
      setTimeout(() => setApiKeySuccess(false), 3000)
    } catch (err) {
      console.error('Error saving Claude settings:', err)
      setApiKeyError('Failed to save settings. Please try again.')
    } finally {
      setSavingApiKey(false)
    }
  }

  const handleFtpHostChange = (e) => {
    setFtpHost(e.target.value)
    setFtpError(null)
    setFtpSuccess(false)
  }

  const handleFtpPortChange = (e) => {
    setFtpPort(e.target.value)
    setFtpError(null)
    setFtpSuccess(false)
  }

  const handleFtpUsernameChange = (e) => {
    setFtpUsername(e.target.value)
    setFtpError(null)
    setFtpSuccess(false)
  }

  const handleFtpDestinationFolderChange = (e) => {
    setFtpDestinationFolder(e.target.value)
    setFtpError(null)
    setFtpSuccess(false)
  }

  const handleFtpPasswordChange = (e) => {
    setFtpPasswordInput(e.target.value)
    setFtpError(null)
    setFtpSuccess(false)
  }

  const handleSaveFtpSettings = async (e) => {
    e.preventDefault()
    setFtpError(null)
    setFtpSuccess(false)

    setSavingFtp(true)
    try {
      const settingsToSave = {}
      
      // Always save host, port, username and destination folder
      settingsToSave.ftpHost = ftpHost.trim()
      settingsToSave.ftpPort = ftpPort.trim()
      settingsToSave.ftpUsername = ftpUsername.trim()
      settingsToSave.ftpDestinationFolder = ftpDestinationFolder.trim()
      
      // Only save password if user entered a new one
      if (ftpPasswordInput.trim()) {
        settingsToSave.ftpPassword = ftpPasswordInput.trim()
        setFtpPassword('***')
        // Clear input after saving for security
        setFtpPasswordInput('')
      }
      
      await saveFtpSettings(settingsToSave)
      setFtpSuccess(true)
      setTimeout(() => setFtpSuccess(false), 3000)
    } catch (err) {
      console.error('Error saving FTP settings:', err)
      setFtpError('Failed to save FTP settings. Please try again.')
    } finally {
      setSavingFtp(false)
    }
  }

  const handleHtmlTemplateChange = (e) => {
    setHtmlTemplate(e.target.value)
    setHtmlError(null)
    setHtmlSuccess(false)
  }

  const handleSaveHtmlTemplate = async (e) => {
    e.preventDefault()
    setHtmlError(null)
    setHtmlSuccess(false)

    setSavingHtml(true)
    try {
      await saveHtmlTemplate(htmlTemplate)
      setHtmlSuccess(true)
      setTimeout(() => setHtmlSuccess(false), 3000)
    } catch (err) {
      console.error('Error saving HTML template:', err)
      setHtmlError('Failed to save HTML template. Please try again.')
    } finally {
      setSavingHtml(false)
    }
  }

  const handleSitemapTemplateChange = (e) => {
    setSitemapTemplate(e.target.value)
    setSitemapError(null)
    setSitemapSuccess(false)
  }

  const handleHtmlIndexTemplateChange = (e) => {
    setHtmlIndexTemplate(e.target.value)
    setHtmlIndexError(null)
    setHtmlIndexSuccess(false)
  }

  const handleSaveHtmlIndexTemplate = async (e) => {
    e.preventDefault()
    setHtmlIndexError(null)
    setHtmlIndexSuccess(false)

    setSavingHtmlIndex(true)
    try {
      await saveHtmlIndexTemplate(htmlIndexTemplate)
      setHtmlIndexSuccess(true)
      setTimeout(() => setHtmlIndexSuccess(false), 3000)
    } catch (err) {
      console.error('Error saving HTML index template:', err)
      setHtmlIndexError('Failed to save HTML index template. Please try again.')
    } finally {
      setSavingHtmlIndex(false)
    }
  }

  const handleDevSecOpsDevTemplateChange = (e) => {
    setDevSecOpsDevTemplate(e.target.value)
    setDevSecOpsTemplatesError(null)
    setDevSecOpsTemplatesSuccess(false)
  }

  const handleDevSecOpsSecTemplateChange = (e) => {
    setDevSecOpsSecTemplate(e.target.value)
    setDevSecOpsTemplatesError(null)
    setDevSecOpsTemplatesSuccess(false)
  }

  const handleDevSecOpsOpsTemplateChange = (e) => {
    setDevSecOpsOpsTemplate(e.target.value)
    setDevSecOpsTemplatesError(null)
    setDevSecOpsTemplatesSuccess(false)
  }

  const handleSaveDevSecOpsArticleIndexTemplates = async (e) => {
    e.preventDefault()
    setDevSecOpsTemplatesError(null)
    setDevSecOpsTemplatesSuccess(false)

    setSavingDevSecOpsTemplates(true)
    try {
      await saveDevSecOpsArticleIndexTemplates({
        dev: devSecOpsDevTemplate,
        sec: devSecOpsSecTemplate,
        ops: devSecOpsOpsTemplate
      })
      setDevSecOpsTemplatesSuccess(true)
      setTimeout(() => setDevSecOpsTemplatesSuccess(false), 3000)
    } catch (err) {
      console.error('Error saving DevSecOps article index templates:', err)
      setDevSecOpsTemplatesError('Failed to save DevSecOps article index templates. Please try again.')
    } finally {
      setSavingDevSecOpsTemplates(false)
    }
  }

  const handleSaveSitemapTemplate = async (e) => {
    e.preventDefault()
    setSitemapError(null)
    setSitemapSuccess(false)

    setSavingSitemap(true)
    try {
      await saveSitemapTemplate(sitemapTemplate)
      setSitemapSuccess(true)
      setTimeout(() => setSitemapSuccess(false), 3000)
    } catch (err) {
      console.error('Error saving sitemap template:', err)
      setSitemapError('Failed to save sitemap template. Please try again.')
    } finally {
      setSavingSitemap(false)
    }
  }

  const handleLinkedInClientIdChange = (e) => {
    setLinkedInClientId(e.target.value)
    setLinkedInError(null)
    setLinkedInSuccess(false)
  }

  const handleLinkedInPrimaryClientSecretChange = (e) => {
    setLinkedInPrimaryClientSecretInput(e.target.value)
    setLinkedInError(null)
    setLinkedInSuccess(false)
  }

  const handleLinkedInSecondaryClientSecretChange = (e) => {
    setLinkedInSecondaryClientSecretInput(e.target.value)
    setLinkedInError(null)
    setLinkedInSuccess(false)
  }

  const handleLinkedInAuthorizedRedirectUrlsChange = (e) => {
    setLinkedInAuthorizedRedirectUrls(e.target.value)
    setLinkedInError(null)
    setLinkedInSuccess(false)
  }

  const handleLinkedInOrganizationIdChange = (e) => {
    setLinkedInOrganizationId(e.target.value)
    setLinkedInError(null)
    setLinkedInSuccess(false)
  }

  const handleLinkedInCompanyPageUrlChange = (e) => {
    setLinkedInCompanyPageUrl(e.target.value)
    setLinkedInError(null)
    setLinkedInSuccess(false)
  }

  const handleSaveLinkedInSettings = async (e) => {
    e.preventDefault()
    setLinkedInError(null)
    setLinkedInSuccess(false)

    setSavingLinkedIn(true)
    try {
      const settingsToSave = {}
      
      // Always save client ID
      settingsToSave.linkedInClientId = linkedInClientId.trim()
      
      // Always save authorized redirect URLs
      settingsToSave.linkedInAuthorizedRedirectUrls = linkedInAuthorizedRedirectUrls.trim()
      
      // Organization ID and Company Page URL (for LinkedIn Posts)
      settingsToSave.linkedInOrganizationId = linkedInOrganizationId.trim()
      settingsToSave.linkedInCompanyPageUrl = linkedInCompanyPageUrl.trim()
      
      // Only save primary client secret if user entered a new one
      if (linkedInPrimaryClientSecretInput.trim()) {
        settingsToSave.linkedInPrimaryClientSecret = linkedInPrimaryClientSecretInput.trim()
        setLinkedInPrimaryClientSecret('***')
        // Clear input after saving for security
        setLinkedInPrimaryClientSecretInput('')
      }
      
      // Only save secondary client secret if user entered a new one
      if (linkedInSecondaryClientSecretInput.trim()) {
        settingsToSave.linkedInSecondaryClientSecret = linkedInSecondaryClientSecretInput.trim()
        setLinkedInSecondaryClientSecret('***')
        // Clear input after saving for security
        setLinkedInSecondaryClientSecretInput('')
      }
      
      await saveLinkedInSettings(settingsToSave)
      setLinkedInSuccess(true)
      setTimeout(() => setLinkedInSuccess(false), 3000)
    } catch (err) {
      console.error('Error saving LinkedIn settings:', err)
      setLinkedInError('Failed to save LinkedIn settings. Please try again.')
    } finally {
      setSavingLinkedIn(false)
    }
  }

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAdmin) {
    return (
      <div className="admin-container">
        <div className="admin-access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
          <button 
            className="btn btn-primary" 
            onClick={() => checkAdminStatus(true)}
            style={{ marginTop: '20px' }}
          >
            Refresh Token & Check Again
          </button>
          {tokenClaims && (
            <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '5px', fontSize: '14px' }}>
              <strong>Current Token Claims:</strong>
              <pre style={{ marginTop: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {JSON.stringify(tokenClaims, null, 2)}
              </pre>
              <p style={{ marginTop: '10px', color: '#666', fontSize: '12px' }}>
                If you just set admin claims, you may need to sign out and sign back in for them to take effect.
              </p>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin Settings</h1>
        <p className="admin-subtitle">Manage system settings and configurations</p>
      </div>
      
      <div className="admin-content">
        <div className="admin-section">
          <div className="admin-section-header admin-section-header-block">
            <div>
              <h2>Claims &amp; access reference</h2>
              <p className="admin-section-description">Which custom claim can access which area. SDR is set manually (e.g. via <code>add-sdr-rights.js</code>).</p>
            </div>
          </div>
          <div className="claims-reference-table-wrap">
            <table className="claims-reference-table">
              <thead>
                <tr>
                  <th>Area</th>
                  <th>Admin</th>
                  <th>Editor</th>
                  <th>SDR</th>
                  <th>Whitelisted only</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Contacts</td><td>✓</td><td>✓</td><td>—</td><td>✓</td></tr>
                <tr><td>Deals</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                <tr><td>Analytics</td><td>✓</td><td>✓</td><td>—</td><td>✓</td></tr>
                <tr><td>Tags</td><td>✓</td><td>✓</td><td>—</td><td>✓</td></tr>
                <tr><td>Files</td><td>✓</td><td>✓</td><td>View/download</td><td>✓</td></tr>
                <tr><td>BANT Ref</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                <tr><td>ICP Hub</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                <tr><td>Pricing Playbook</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                <tr><td>Price List</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                <tr><td>AISec Markets</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                <tr><td>AISec Scripts</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                <tr><td>Enquiry Response Checklist</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                <tr><td>Prospect Research (Individuals)</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                <tr><td>Company List Building</td><td>✓</td><td>✓</td><td>✓</td><td>✓</td></tr>
                <tr><td>Job Role Analysis</td><td>✓</td><td>✓</td><td>—</td><td>✓</td></tr>
                <tr><td>Filesig Website Articles</td><td>✓</td><td>✓</td><td>—</td><td>—</td></tr>
                <tr><td>DevSecOps Website Articles</td><td>✓</td><td>✓</td><td>—</td><td>—</td></tr>
                <tr><td>LinkedIn Posts</td><td>✓</td><td>✓</td><td>—</td><td>—</td></tr>
                <tr><td>Ad Image Creator</td><td>✓</td><td>✓</td><td>—</td><td>—</td></tr>
                <tr><td>Investment</td><td>✓</td><td>—</td><td>—</td><td>—</td></tr>
                <tr><td>Admin Settings</td><td>✓</td><td>—</td><td>—</td><td>—</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="admin-section">
          <div className="admin-section-header">
            <h2>Claude AI Settings</h2>
          </div>
          
          <div className="settings-form-container">
            <form onSubmit={handleSaveClaudeSettings} className="settings-form">
              <div className="form-group">
                <label htmlFor="claude-api-key">
                  Claude AI API Key
                  <span className="form-help-text">
                    <a 
                      href="https://platform.claude.com/settings/keys" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="external-link"
                    >
                      Get your API key from Claude Platform
                    </a>
                  </span>
                </label>
                <input
                  type="password"
                  id="claude-api-key"
                  name="claudeApiKey"
                  value={claudeApiKeyInput}
                  onChange={handleClaudeApiKeyChange}
                  placeholder="Enter your Claude AI API key"
                  className={apiKeyError ? 'input-error' : ''}
                />
                {claudeApiKey && !claudeApiKeyInput && (
                  <p className="form-hint">
                    ✓ API key is saved. Enter a new key to update it.
                  </p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="claude-default-prompt">
                  Default Prompt
                  <span className="form-help-text">
                    Enter a default prompt that will be used for Claude AI interactions
                  </span>
                </label>
                <textarea
                  id="claude-default-prompt"
                  name="claudeDefaultPrompt"
                  value={claudeDefaultPrompt}
                  onChange={handleClaudeDefaultPromptChange}
                  placeholder="Enter your default prompt for Claude AI..."
                  rows="6"
                  className={apiKeyError ? 'input-error' : ''}
                />
              </div>

              {apiKeyError && (
                <div className="form-error">
                  {apiKeyError}
                </div>
              )}

              {apiKeySuccess && (
                <div className="form-success">
                  Settings saved successfully!
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={savingApiKey}
                >
                  {savingApiKey ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="admin-section">
          <div className="admin-section-header">
            <h2>FTP Settings</h2>
          </div>
          
          <div className="settings-form-container">
            <form onSubmit={handleSaveFtpSettings} className="settings-form">
              <div className="form-group">
                <label htmlFor="ftp-host">
                  FTP Host
                  <span className="form-help-text">
                    Enter the hostname or IP address of your FTP server
                  </span>
                </label>
                <input
                  type="text"
                  id="ftp-host"
                  name="ftpHost"
                  value={ftpHost}
                  onChange={handleFtpHostChange}
                  placeholder="ftp.example.com or 192.168.1.1"
                  className={ftpError ? 'input-error' : ''}
                />
              </div>

              <div className="form-group">
                <label htmlFor="ftp-port">
                  FTP Port
                  <span className="form-help-text">
                    Enter the port number for your FTP server (default: 21)
                  </span>
                </label>
                <input
                  type="number"
                  id="ftp-port"
                  name="ftpPort"
                  value={ftpPort}
                  onChange={handleFtpPortChange}
                  placeholder="21"
                  className={ftpError ? 'input-error' : ''}
                />
              </div>

              <div className="form-group">
                <label htmlFor="ftp-username">
                  FTP Username
                  <span className="form-help-text">
                    Enter the username for your FTP server
                  </span>
                </label>
                <input
                  type="text"
                  id="ftp-username"
                  name="ftpUsername"
                  value={ftpUsername}
                  onChange={handleFtpUsernameChange}
                  placeholder="Enter FTP username"
                  className={ftpError ? 'input-error' : ''}
                />
              </div>

              <div className="form-group">
                <label htmlFor="ftp-destination-folder">
                  Destination Folder
                  <span className="form-help-text">
                    Enter the destination folder path on the FTP server (e.g., /uploads/files)
                  </span>
                </label>
                <input
                  type="text"
                  id="ftp-destination-folder"
                  name="ftpDestinationFolder"
                  value={ftpDestinationFolder}
                  onChange={handleFtpDestinationFolderChange}
                  placeholder="Enter destination folder path"
                  className={ftpError ? 'input-error' : ''}
                />
              </div>

              <div className="form-group">
                <label htmlFor="ftp-password">
                  FTP Password
                  <span className="form-help-text">
                    Enter the password for your FTP server
                  </span>
                </label>
                <input
                  type="password"
                  id="ftp-password"
                  name="ftpPassword"
                  value={ftpPasswordInput}
                  onChange={handleFtpPasswordChange}
                  placeholder="Enter FTP password"
                  className={ftpError ? 'input-error' : ''}
                />
                {ftpPassword && !ftpPasswordInput && (
                  <p className="form-hint">
                    ✓ Password is saved. Enter a new password to update it.
                  </p>
                )}
              </div>

              {ftpError && (
                <div className="form-error">
                  {ftpError}
                </div>
              )}

              {ftpSuccess && (
                <div className="form-success">
                  FTP settings saved successfully!
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={savingFtp}
                >
                  {savingFtp ? 'Saving...' : 'Save FTP Settings'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="admin-section">
          <div className="admin-section-header">
            <h2>HTML Template</h2>
          </div>
          
          <div className="settings-form-container">
            <form onSubmit={handleSaveHtmlTemplate} className="settings-form">
              <div className="form-group">
                <label htmlFor="html-template">
                  HTML Template
                  <span className="form-help-text">
                    Enter the complete HTML template code (including header, body content, and footer)
                  </span>
                </label>
                <textarea
                  id="html-template"
                  name="htmlTemplate"
                  value={htmlTemplate}
                  onChange={handleHtmlTemplateChange}
                  placeholder="Enter complete HTML template code..."
                  rows="20"
                  className={htmlError ? 'input-error' : ''}
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>

              {htmlError && (
                <div className="form-error">
                  {htmlError}
                </div>
              )}

              {htmlSuccess && (
                <div className="form-success">
                  HTML template saved successfully!
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={savingHtml}
                >
                  {savingHtml ? 'Saving...' : 'Save HTML Template'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="admin-section">
          <div className="admin-section-header">
            <h2>HTML Index Template</h2>
          </div>

          <div className="settings-form-container">
            <form onSubmit={handleSaveHtmlIndexTemplate} className="settings-form">
              <div className="form-group">
                <label htmlFor="html-index-template">
                  HTML Index Template (listing articles)
                  <span className="form-help-text">
                    Enter the HTML template used for the index page that lists articles (e.g. links and summaries)
                  </span>
                </label>
                <textarea
                  id="html-index-template"
                  name="htmlIndexTemplate"
                  value={htmlIndexTemplate}
                  onChange={handleHtmlIndexTemplateChange}
                  placeholder="Enter HTML index template for listing articles..."
                  rows="20"
                  className={htmlIndexError ? 'input-error' : ''}
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>

              {htmlIndexError && (
                <div className="form-error">
                  {htmlIndexError}
                </div>
              )}

              {htmlIndexSuccess && (
                <div className="form-success">
                  HTML index template saved successfully!
                </div>
              )}

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={savingHtmlIndex}
                >
                  {savingHtmlIndex ? 'Saving...' : 'Save HTML Index Template'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="admin-section">
          <div className="admin-section-header">
            <h2>DevSecOps Article Index Templates</h2>
          </div>

          <div className="settings-form-container">
            <form onSubmit={handleSaveDevSecOpsArticleIndexTemplates} className="settings-form">
              <div className="form-group">
                <label htmlFor="devsecops-dev-template">
                  Dev Template
                  <span className="form-help-text">
                    JSON template for the Dev article index
                  </span>
                </label>
                <textarea
                  id="devsecops-dev-template"
                  name="devSecOpsDevTemplate"
                  value={devSecOpsDevTemplate}
                  onChange={handleDevSecOpsDevTemplateChange}
                  placeholder="Enter JSON template for Dev articles index..."
                  rows="12"
                  className={devSecOpsTemplatesError ? 'input-error' : ''}
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="devsecops-sec-template">
                  Sec Template
                  <span className="form-help-text">
                    JSON template for the Sec article index
                  </span>
                </label>
                <textarea
                  id="devsecops-sec-template"
                  name="devSecOpsSecTemplate"
                  value={devSecOpsSecTemplate}
                  onChange={handleDevSecOpsSecTemplateChange}
                  placeholder="Enter JSON template for Sec articles index..."
                  rows="12"
                  className={devSecOpsTemplatesError ? 'input-error' : ''}
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="devsecops-ops-template">
                  Ops Template
                  <span className="form-help-text">
                    JSON template for the Ops article index
                  </span>
                </label>
                <textarea
                  id="devsecops-ops-template"
                  name="devSecOpsOpsTemplate"
                  value={devSecOpsOpsTemplate}
                  onChange={handleDevSecOpsOpsTemplateChange}
                  placeholder="Enter JSON template for Ops articles index..."
                  rows="12"
                  className={devSecOpsTemplatesError ? 'input-error' : ''}
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>

              {devSecOpsTemplatesError && (
                <div className="form-error">
                  {devSecOpsTemplatesError}
                </div>
              )}

              {devSecOpsTemplatesSuccess && (
                <div className="form-success">
                  DevSecOps article index templates saved successfully!
                </div>
              )}

              <div className="form-actions">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={savingDevSecOpsTemplates}
                >
                  {savingDevSecOpsTemplates ? 'Saving...' : 'Save DevSecOps Article Index Templates'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="admin-section">
          <div className="admin-section-header">
            <h2>Sitemap Template</h2>
          </div>

          <div className="settings-form-container">
            <form onSubmit={handleSaveSitemapTemplate} className="settings-form">
              <div className="form-group">
                <label htmlFor="sitemap-template">
                  Sitemap Template
                  <span className="form-help-text">
                    Enter the sitemap template code (XML format for sitemap generation)
                  </span>
                </label>
                <textarea
                  id="sitemap-template"
                  name="sitemapTemplate"
                  value={sitemapTemplate}
                  onChange={handleSitemapTemplateChange}
                  placeholder="Enter sitemap template code..."
                  rows="20"
                  className={sitemapError ? 'input-error' : ''}
                  style={{ fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>

              {sitemapError && (
                <div className="form-error">
                  {sitemapError}
                </div>
              )}

              {sitemapSuccess && (
                <div className="form-success">
                  Sitemap template saved successfully!
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={savingSitemap}
                >
                  {savingSitemap ? 'Saving...' : 'Save Sitemap Template'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="admin-section">
          <div className="admin-section-header">
            <h2>LinkedIn Settings</h2>
          </div>
          
          <div className="settings-form-container">
            <form onSubmit={handleSaveLinkedInSettings} className="settings-form">
              <div className="form-group">
                <label htmlFor="linkedin-client-id">
                  Client ID
                  <span className="form-help-text">
                    Enter your LinkedIn application client ID
                  </span>
                </label>
                <input
                  type="text"
                  id="linkedin-client-id"
                  name="linkedInClientId"
                  value={linkedInClientId}
                  onChange={handleLinkedInClientIdChange}
                  placeholder="Enter LinkedIn Client ID"
                  className={linkedInError ? 'input-error' : ''}
                />
              </div>

              <div className="form-group">
                <label htmlFor="linkedin-primary-client-secret">
                  Primary Client Secret
                  <span className="form-help-text">
                    Enter your LinkedIn application primary client secret
                  </span>
                </label>
                <input
                  type="password"
                  id="linkedin-primary-client-secret"
                  name="linkedInPrimaryClientSecret"
                  value={linkedInPrimaryClientSecretInput}
                  onChange={handleLinkedInPrimaryClientSecretChange}
                  placeholder="Enter Primary Client Secret"
                  className={linkedInError ? 'input-error' : ''}
                />
                {linkedInPrimaryClientSecret && !linkedInPrimaryClientSecretInput && (
                  <p className="form-hint">
                    ✓ Primary client secret is saved. Enter a new secret to update it.
                  </p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="linkedin-secondary-client-secret">
                  Secondary Client Secret
                  <span className="form-help-text">
                    Enter your LinkedIn application secondary client secret
                  </span>
                </label>
                <input
                  type="password"
                  id="linkedin-secondary-client-secret"
                  name="linkedInSecondaryClientSecret"
                  value={linkedInSecondaryClientSecretInput}
                  onChange={handleLinkedInSecondaryClientSecretChange}
                  placeholder="Enter Secondary Client Secret"
                  className={linkedInError ? 'input-error' : ''}
                />
                {linkedInSecondaryClientSecret && !linkedInSecondaryClientSecretInput && (
                  <p className="form-hint">
                    ✓ Secondary client secret is saved. Enter a new secret to update it.
                  </p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="linkedin-authorized-redirect-urls">
                  Authorized Redirect URLs for your app
                  <span className="form-help-text">
                    Enter the authorized redirect URLs for your LinkedIn application (one per line or comma-separated)
                  </span>
                </label>
                <textarea
                  id="linkedin-authorized-redirect-urls"
                  name="linkedInAuthorizedRedirectUrls"
                  value={linkedInAuthorizedRedirectUrls}
                  onChange={handleLinkedInAuthorizedRedirectUrlsChange}
                  placeholder="https://example.com/callback&#10;https://example.com/auth/callback"
                  rows="4"
                  className={linkedInError ? 'input-error' : ''}
                />
              </div>

              <div className="form-group">
                <label htmlFor="linkedin-organization-id">
                  Organization ID *
                  <span className="form-help-text">
                    LinkedIn organization numeric ID used when posting to your company page (e.g., 105360915)
                  </span>
                </label>
                <input
                  type="text"
                  id="linkedin-organization-id"
                  name="linkedInOrganizationId"
                  value={linkedInOrganizationId}
                  onChange={handleLinkedInOrganizationIdChange}
                  placeholder="105360915"
                  className={linkedInError ? 'input-error' : ''}
                />
              </div>

              <div className="form-group">
                <label htmlFor="linkedin-company-page-url">
                  Company Page URL (optional)
                  <span className="form-help-text">
                    LinkedIn company page URL for reference (e.g., https://www.linkedin.com/company/filesig/)
                  </span>
                </label>
                <input
                  type="text"
                  id="linkedin-company-page-url"
                  name="linkedInCompanyPageUrl"
                  value={linkedInCompanyPageUrl}
                  onChange={handleLinkedInCompanyPageUrlChange}
                  placeholder="https://www.linkedin.com/company/filesig/"
                  className={linkedInError ? 'input-error' : ''}
                />
              </div>

              {linkedInError && (
                <div className="form-error">
                  {linkedInError}
                </div>
              )}

              {linkedInSuccess && (
                <div className="form-success">
                  LinkedIn settings saved successfully!
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={savingLinkedIn}
                >
                  {savingLinkedIn ? 'Saving...' : 'Save LinkedIn Settings'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="admin-section">
          <div className="admin-section-header">
            <h2>Products</h2>
            <button 
              className="btn btn-primary"
              onClick={() => {
                if (showProductForm) {
                  handleCancelEdit()
                } else {
                  setShowProductForm(true)
                }
              }}
            >
              {showProductForm ? 'Cancel' : '+ Add Product'}
            </button>
          </div>

          {showProductForm && (
            <div ref={productFormRef} className="product-form-container">
              {editingProductId && (
                <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'rgba(37, 99, 235, 0.1)', border: '1px solid var(--primary)', borderRadius: '6px', color: 'var(--primary)', fontSize: '0.875rem' }}>
                  ✏️ Editing product: {productForm.name || 'Untitled'}
                </div>
              )}
              <form onSubmit={handleProductSubmit} className="product-form">
                <div className="form-group">
                  <label htmlFor="product-name">Product Name *</label>
                  <input
                    type="text"
                    id="product-name"
                    name="name"
                    value={productForm.name}
                    onChange={handleProductFormChange}
                    placeholder="Enter product name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="product-description">Description</label>
                  <textarea
                    id="product-description"
                    name="description"
                    value={productForm.description}
                    onChange={handleProductFormChange}
                    placeholder="Enter product description"
                    rows="4"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="product-url">URL *</label>
                  <input
                    type="url"
                    id="product-url"
                    name="url"
                    value={productForm.url}
                    onChange={handleProductFormChange}
                    placeholder="https://example.com"
                    required
                  />
                </div>

                <div className="form-section-divider">
                  <h3>CTA Button</h3>
                </div>

                <div className="form-group">
                  <label htmlFor="cta-button-url">CTA Button URL</label>
                  <input
                    type="url"
                    id="cta-button-url"
                    name="ctaButtonUrl"
                    value={productForm.ctaButtonUrl}
                    onChange={handleProductFormChange}
                    placeholder="https://example.com/cta"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="cta-button-text">CTA Button Text</label>
                  <input
                    type="text"
                    id="cta-button-text"
                    name="ctaButtonText"
                    value={productForm.ctaButtonText}
                    onChange={handleProductFormChange}
                    placeholder="Get Started"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="cta-note">CTA Note</label>
                  <textarea
                    id="cta-note"
                    name="ctaNote"
                    value={productForm.ctaNote}
                    onChange={handleProductFormChange}
                    placeholder="Enter CTA note or instructions"
                    rows="3"
                  />
                </div>

                <div className="form-section-divider">
                  <h3>Demo Button</h3>
                </div>

                <div className="form-group">
                  <label htmlFor="demo-button-url">Demo Button URL</label>
                  <input
                    type="url"
                    id="demo-button-url"
                    name="demoButtonUrl"
                    value={productForm.demoButtonUrl}
                    onChange={handleProductFormChange}
                    placeholder="https://example.com/demo"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="demo-button-text">Demo Button Text</label>
                  <input
                    type="text"
                    id="demo-button-text"
                    name="demoButtonText"
                    value={productForm.demoButtonText}
                    onChange={handleProductFormChange}
                    placeholder="Watch Demo"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="demo-note">Demo Note</label>
                  <textarea
                    id="demo-note"
                    name="demoNote"
                    value={productForm.demoNote}
                    onChange={handleProductFormChange}
                    placeholder="Enter demo note or instructions"
                    rows="3"
                  />
                </div>

                {error && (
                  <div className="form-error">
                    {error}
                  </div>
                )}

                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting 
                      ? (editingProductId ? 'Updating...' : 'Creating...') 
                      : (editingProductId ? 'Update Product' : 'Create Product')
                    }
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="products-list">
            {products.length === 0 ? (
              <div className="empty-state">
                <p>No products yet. Click "Add Product" to create one.</p>
              </div>
            ) : (
              <div className="products-grid">
                {products.map((product) => (
                  <div key={product.id} className="product-card">
                    <div className="product-card-header">
                      <h3>{product.name}</h3>
                      <div className="product-card-actions">
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => handleEditProduct(product)}
                          title="Edit product"
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => handleDeleteProduct(product.id)}
                          title="Delete product"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    {product.description && (
                      <p className="product-description">{product.description}</p>
                    )}
                    <div className="product-url">
                      <a 
                        href={product.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        {product.url}
                      </a>
                    </div>
                    
                    {(product.ctaButtonUrl || product.demoButtonUrl) && (
                      <div className="product-buttons">
                        {product.ctaButtonUrl && (
                          <div className="product-button-group">
                            {product.ctaButtonText ? (
                              <a 
                                href={product.ctaButtonUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="btn btn-cta"
                              >
                                {product.ctaButtonText}
                              </a>
                            ) : (
                              <a 
                                href={product.ctaButtonUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="product-button-link"
                              >
                                {product.ctaButtonUrl}
                              </a>
                            )}
                            {product.ctaNote && (
                              <p className="product-button-note">{product.ctaNote}</p>
                            )}
                          </div>
                        )}
                        
                        {product.demoButtonUrl && (
                          <div className="product-button-group">
                            {product.demoButtonText ? (
                              <a 
                                href={product.demoButtonUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="btn btn-demo"
                              >
                                {product.demoButtonText}
                              </a>
                            ) : (
                              <a 
                                href={product.demoButtonUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="product-button-link"
                              >
                                {product.demoButtonUrl}
                              </a>
                            )}
                            {product.demoNote && (
                              <p className="product-button-note">{product.demoNote}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Admin